import { t } from "@lingui/macro";
import { ArrowsLeftRight, CaretLeft, Lightning, PaintBrush, Swatches } from "@phosphor-icons/react";
import {
  Button,
  ScrollArea,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
} from "@reactive-resume/ui";

import { ATSScoreSection } from "./sections/ats-score";
import { ExportSection } from "./sections/export";
import { JobMatchingSection } from "./sections/job-matching";
import { PageSection } from "./sections/page";
import { TemplateSection } from "./sections/template";
import { ThemeSection } from "./sections/theme";
import { TypographySection } from "./sections/typography";

type RightSidebarProps = {
  collapsed?: boolean;
  onExpand?: () => void;
  onCollapse?: () => void;
};

export const RightSidebar = ({ collapsed, onExpand, onCollapse }: RightSidebarProps) => {
  if (collapsed) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center border-l bg-secondary-accent py-4">
        <Tooltip content={t`Expand sidebar`} side="left">
          <Button
            size="icon"
            variant="ghost"
            className="h-9 w-9"
            onClick={onExpand}
          >
            <CaretLeft size={18} weight="bold" />
          </Button>
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden bg-secondary-accent"
      data-testid="builder-right-sidebar"
    >
      <Tabs defaultValue="template" className="flex h-full min-h-0 flex-col overflow-hidden">
        <div className="shrink-0 border-b px-2 pt-2 pb-0">
          <TabsList
            className="grid h-auto w-full min-w-0 grid-cols-2 gap-0.5 p-0.5"
            style={{ display: "grid" }}
          >
            <TabsTrigger value="template" className="min-w-0 flex-1 gap-x-1 px-1.5 text-xs">
              <Swatches size={14} className="shrink-0" />
              <span className="truncate">{t`Template`}</span>
            </TabsTrigger>
            <TabsTrigger value="style" className="min-w-0 flex-1 gap-x-1 px-1.5 text-xs">
              <PaintBrush size={14} className="shrink-0" />
              <span className="truncate">{t`Style`}</span>
            </TabsTrigger>
            <TabsTrigger value="ai-tools" className="min-w-0 flex-1 gap-x-1 px-1.5 text-xs">
              <Lightning size={14} weight="fill" className="shrink-0" />
              <span className="truncate">{t`AI Tools`}</span>
            </TabsTrigger>
            <TabsTrigger value="export" className="min-w-0 flex-1 gap-x-1 px-1.5 text-xs">
              <ArrowsLeftRight size={14} className="shrink-0" />
              <span className="truncate">{t`Export`}</span>
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="relative min-h-0 flex-1 overflow-hidden">
          <TabsContent
            value="template"
            className="absolute inset-0 mt-0 flex flex-col overflow-hidden data-[state=inactive]:hidden"
          >
            <ScrollArea orientation="vertical" className="h-full min-h-0" fadeEdges scrollHint>
              <div className="grid min-w-0 gap-y-3 p-3 pb-8 @container/right">
                <TemplateSection />
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent
            value="style"
            className="absolute inset-0 mt-0 flex flex-col overflow-hidden data-[state=inactive]:hidden"
          >
            <ScrollArea orientation="vertical" className="h-full min-h-0" fadeEdges scrollHint>
              <div className="grid min-w-0 gap-y-3 p-3 pb-8 @container/right">
                <TypographySection />
                <Separator />
                <ThemeSection />
                <Separator />
                <PageSection />
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent
            value="ai-tools"
            className="absolute inset-0 mt-0 flex flex-col overflow-hidden data-[state=inactive]:hidden"
          >
            <ScrollArea orientation="vertical" className="h-full min-h-0" fadeEdges scrollHint>
              <div className="grid min-w-0 gap-y-4 p-3 pb-8">
                <ATSScoreSection />
                <Separator />
                <JobMatchingSection />
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent
            value="export"
            className="absolute inset-0 mt-0 flex flex-col overflow-hidden data-[state=inactive]:hidden"
          >
            <ScrollArea orientation="vertical" className="h-full min-h-0" fadeEdges scrollHint>
              <div className="min-w-0 p-3 pb-8">
                <ExportSection />
              </div>
            </ScrollArea>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};
